/* eslint-disable react/jsx-props-no-spreading -- react-hook-form Controller `field` */
import { Stack, Typography } from '@mui/material';
import { FormStepComponentProps, SIMDocumentTypeAttachmentOption, TextField } from 'common';
import { useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { PostSignupFormSchema } from '../schemas';
import { SimAttachmentsDropzone } from '../SimAttachmentsDropzone';
import { ServiceAgreementConsent } from '../ServiceAgreementConsent';
import { PostSignupAgreement, isSovosAgreementNeeded } from '../../usePostSignupAgreements';
import { LockedFormNotice, LockedFormStatus } from '../LockedFormNotice';

type Props = FormStepComponentProps<typeof PostSignupFormSchema> & {
    attachmentOption: SIMDocumentTypeAttachmentOption;
    files: File[];
    onFilesChange: (files: File[]) => void;
    hasError: boolean;
    agreement: PostSignupAgreement | undefined;
    agreementAccepted: boolean;
    onAgreementAcceptedChange: (accepted: boolean) => void;
    lockedStatus: LockedFormStatus | null;
};

export function TaxDetailsStep({
    control,
    attachmentOption,
    files,
    onFilesChange,
    hasError,
    agreement,
    agreementAccepted,
    onAgreementAcceptedChange,
    lockedStatus,
}: Props) {
    const tin = useWatch({ name: 'taxDetails.TIN', control, exact: true });
    const shownAgreement = isSovosAgreementNeeded(tin) ? agreement : undefined;

    useEffect(() => {
        if (shownAgreement == null && agreementAccepted) {
            onAgreementAcceptedChange(false);
        }
    }, [shownAgreement, agreementAccepted, onAgreementAcceptedChange]);

    return (
        <Stack spacing={3} p={{ xs: 2, sm: 0 }}>
            <Typography variant="subtitle1">Tax Details (W-9)</Typography>
            <Typography variant="body2" color="text.secondary">
                Please enter your applicable tax information. U.S. businesses should provide a TIN (EIN/SSN), and
                VAT-registered businesses should provide their VAT registration number.
            </Typography>
            <LockedFormNotice status={lockedStatus} />
            <Controller
                name="taxDetails.BusinessName"
                control={control}
                rules={{
                    deps: ['taxDetails.TIN'],
                }}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="Business Name"
                        required={!!tin}
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="tax-details-business-name"
                    />
                )}
            />
            <Controller
                name="taxDetails.TIN"
                control={control}
                rules={{
                    deps: ['taxDetails.VATNumber', 'taxDetails.BusinessName'],
                }}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="TIN (EIN/SSN)"
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="tax-details-tin"
                    />
                )}
            />
            <Controller
                name="taxDetails.VATNumber"
                control={control}
                rules={{
                    deps: ['taxDetails.TIN'],
                }}
                render={({ field, fieldState }) => (
                    <TextField
                        {...field}
                        label="VAT Number"
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        testId="tax-details-vat"
                    />
                )}
            />
            <SimAttachmentsDropzone
                attachmentOption={attachmentOption}
                files={files}
                onFilesChange={onFilesChange}
                hasError={hasError}
                testId="tax-details-attachments"
            />
            {shownAgreement != null && (
                <ServiceAgreementConsent
                    agreement={shownAgreement}
                    stepKey="taxDetails"
                    accepted={agreementAccepted}
                    onAcceptedChange={onAgreementAcceptedChange}
                />
            )}
        </Stack>
    );
}
